import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"
import { qk } from "./api"

export type GeneralSettings = {
  store_name: string
  store_email: string
  store_phone: string
  legal_name: string
  address: { address_1: string; address_2: string; city: string; province: string; postal_code: string; country_code: string }
  unit_system: "imperial" | "metric"
  weight_unit: "lb" | "oz" | "kg" | "g"
  timezone: string
  order_prefix: string
  order_suffix: string
  auto_fulfill: "none" | "all" | "gift_cards"
  auto_archive: boolean
}

export function useGeneralSettings() {
  return useQuery({
    queryKey: ["settings", "general"],
    queryFn: () => sdk.client.fetch<{ settings: GeneralSettings }>("/admin/terify/settings/general"),
  })
}

export function useSaveGeneralSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: GeneralSettings) => sdk.client.fetch<{ settings: GeneralSettings }>("/admin/terify/settings/general", { method: "POST", body }),
    onSuccess: (r) => {
      qc.setQueryData(["settings", "general"], r)
      qc.invalidateQueries({ queryKey: qk.me })
    },
  })
}

export type OnlineStoreSettings = {
  title: string
  meta_description: string
  social_image: string
  password_enabled: boolean
  password_message: string
  password_set: boolean
}
export function useOnlineStore() {
  return useQuery({ queryKey: ["settings", "online-store"], queryFn: () => sdk.client.fetch<{ settings: OnlineStoreSettings }>("/admin/terify/settings/online-store") })
}
export function useSaveOnlineStore() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Omit<OnlineStoreSettings, "password_set"> & { password?: string }) => sdk.client.fetch<{ settings: OnlineStoreSettings }>("/admin/terify/settings/online-store", { method: "POST", body }),
    onSuccess: (r) => qc.setQueryData(["settings", "online-store"], r),
  })
}

export type FieldMode = "hidden" | "optional" | "required"
export type CheckoutSettings = { company: FieldMode; address_2: FieldMode; phone: FieldMode; abandoned_email_hours: 0 | 1 | 6 | 10 | 24 }
export function useCheckoutSettings() {
  return useQuery({ queryKey: ["settings", "checkout"], queryFn: () => sdk.client.fetch<{ settings: CheckoutSettings }>("/admin/terify/settings/checkout") })
}
export function useSaveCheckoutSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CheckoutSettings) => sdk.client.fetch<{ settings: CheckoutSettings }>("/admin/terify/settings/checkout", { method: "POST", body }),
    onSuccess: (r) => qc.setQueryData(["settings", "checkout"], r),
  })
}

export type AccountSettings = { enabled: boolean }
export function useAccountSettings() {
  return useQuery({ queryKey: ["settings", "customer-accounts"], queryFn: () => sdk.client.fetch<{ settings: AccountSettings }>("/admin/terify/settings/customer-accounts") })
}
export function useSaveAccountSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AccountSettings) => sdk.client.fetch<{ settings: AccountSettings }>("/admin/terify/settings/customer-accounts", { method: "POST", body }),
    onSuccess: (r) => qc.setQueryData(["settings", "customer-accounts"], r),
  })
}

export type TrackingSettings = { meta_pixel_id: string; ga4_id: string; tiktok_pixel_id: string }
export type PrivacySettings = { banner: boolean; message: string; policy_url: string }
function useSettingPair<T>(slug: string) {
  const path = `/admin/terify/settings/${slug}`
  const key = ["settings", slug]
  const qc = useQueryClient()
  const get = useQuery({ queryKey: key, queryFn: () => sdk.client.fetch<{ settings: T }>(path) })
  const save = useMutation({
    mutationFn: (body: T) => sdk.client.fetch<{ settings: T }>(path, { method: "POST", body: body as any }),
    onSuccess: (r) => qc.setQueryData(key, r),
  })
  return { get, save }
}
export const useTrackingSettings = () => useSettingPair<TrackingSettings>("customer-events")
export const usePrivacySettings = () => useSettingPair<PrivacySettings>("customer-privacy")

export type PaymentSettings = { capture: "automatic" | "manual" }
export type GatewayStatus = { provider: string; connected: boolean; hosted_form_ready: boolean; environment: "sandbox" | "production"; login_id: string | null }
/** Apple Pay / Google Pay (backend lib/wallets.ts). */
export type WalletStatus = {
  settings: { apple_pay: boolean; google_pay: boolean; google_merchant_id: string; apple_domain_file_set: boolean }
  google_pay: { gateway_id_found: boolean; needs_merchant_id: boolean; ready: boolean }
  apple_pay: {
    merchant_id: string | null
    certificate_set: boolean
    /** env = the hosting team's secrets; settings = set up on this page. */
    source?: "env" | "settings" | null
    setup?: { merchant_id: string | null; csr_created_at: string | null; certificate: { expires_at: string | null } | null } | null
    domain_file_set: boolean
    ready: boolean
  }
}
export type WalletInput = Partial<{ apple_pay: boolean; google_pay: boolean; google_merchant_id: string; apple_domain_file: string }>
type PaymentsResponse = { settings: PaymentSettings; gateway: GatewayStatus; wallets: WalletStatus }
export function usePaymentSettings() {
  return useQuery({ queryKey: ["settings", "payments"], queryFn: () => sdk.client.fetch<PaymentsResponse>("/admin/terify/settings/payments") })
}
export function useSavePaymentSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<PaymentSettings> & { wallets?: WalletInput }) => sdk.client.fetch<PaymentsResponse>("/admin/terify/settings/payments", { method: "POST", body }),
    onSuccess: (r) => qc.setQueryData(["settings", "payments"], r),
  })
}

/** Settings → Brand: logos, favicon, colours, slogan (store.metadata.terify_brand). */
export type BrandSettings = {
  logo: string
  logo_dark: string
  square_logo: string
  favicon: string
  primary_color: string
  accent_color: string
  ink_color: string
  slogan: string
  short_description: string
  /** Product rows in customer emails: the product photo, no image, or one image for every item. */
  email_item_images?: "product" | "none" | "custom"
  email_item_image?: string
}
export function useBrand() {
  return useQuery({ queryKey: ["settings", "brand"], queryFn: () => sdk.client.fetch<{ brand: BrandSettings }>("/admin/terify/settings/brand") })
}
export function useSaveBrand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: BrandSettings) => sdk.client.fetch<{ brand: BrandSettings }>("/admin/terify/settings/brand", { method: "POST", body }),
    onSuccess: (r) => qc.setQueryData(["settings", "brand"], r),
  })
}
